/**
 * Time series chart handling for weather and sensor data
 */

import { API_BASE } from '../config.js';
import { getChart } from './state.js';

/**
 * Load and display weather time series data
 * @param {string} chartType - Type of chart clicked (wind, dp, uv, precip, lightning)
 */
export function loadWeatherTimeseries(chartType) {
  const chart = getChart('timeSeries');
  if (!chart) {
    console.error('[TimeSeries] Chart not initialized');
    return;
  }

  // Determine API parameters based on chart type
  let url = `${API_BASE}/tempest-observations`;
  let params;

  switch (chartType) {
  case 'dp':
    params = 't:dp';
    break;
  case 'wind':
    params = 'windg:winda:windl';
    break;
  case 'uv':
    params = 'uv:sr';
    break;
  case 'lightning':
    params = 'lightning';
    url = `${API_BASE}/tempest-events`;
    break;
  default:
    params = chartType;
  }

  // Fetch data and update chart
  jQuery.getJSON(url, { v: params }, (data) => {
    // Remove all existing series
    for (let i = chart.series.length - 1; i >= 0; i--) {
      chart.series[i].remove(false);
    }

    // Remove extra axis if exists
    if (chart.get('second-axis')) {
      chart.get('second-axis').remove();
    }

    // Handle different chart types
    if (chartType === 'uv') {
      addUVSeries(chart, data);
    } else if (chartType === 'lightning') {
      addLightningSeries(chart, data);
    } else {
      addGeneralSeries(chart, data, chartType);
    }

    // Update chart
    chart.xAxis[0].update({ max: Date.now() });
    chart.zoomOut();
    chart.redraw();
  }).fail((jqXHR, textStatus, errorThrown) => {
    console.error('[TimeSeries] Failed to load data:', textStatus, errorThrown);
  });
}

/**
 * Add UV and Solar Radiation series to chart
 */
function addUVSeries(chart, data) {
  // Get UV zones from the UV gauge chart
  const chartUV = getChart('uv');
  if (chartUV) {
    const plotBands = chartUV.yAxis[0].plotLinesAndBands;
    const zones = [];

    plotBands.forEach((p) => {
      zones.push({
        value: p.options.to,
        color: p.options.color,
      });
    });

    // Add UV Index series with color zones
    chart.addSeries({
      type: 'bar',
      name: 'UV Index',
      data: data['d']['uv']['values'],
      zones: zones,
    }, false);
  } else {
    // Fallback if UV chart not available
    chart.addSeries({
      type: 'bar',
      name: 'UV Index',
      data: data['d']['uv']['values'],
    }, false);
  }

  chart.yAxis[0].setTitle({ text: data['d']['uv']['type'] });

  // Add Solar Radiation on second axis
  chart.addAxis({
    id: 'second-axis',
    title: {
      text: `${data['d']['sr']['type']} (${data['d']['sr']['units']})`,
    },
    opposite: true,
  });

  chart.addSeries({
    type: 'spline',
    name: 'Solar Radiation',
    data: data['d']['sr']['values'],
    yAxis: 1,
  }, false);
}

/**
 * Add lightning strike series to chart
 */
function addLightningSeries(chart, data) {
  chart.addSeries({
    type: 'scatter',
    name: data['d']['name'],
    data: data['d']['values'],
  }, false);

  chart.yAxis[0].setTitle({
    text: `${data['d']['type']} (${data['d']['units']})`,
  });
}

/**
 * Add general weather series to chart
 */
function addGeneralSeries(chart, data, chartType) {
  const chartStyle = chartType === 'wind' ? 'area' : 'line';

  jQuery.each(data['d'], (k, v) => {
    chart.addSeries({
      type: chartStyle,
      name: v['name'],
      data: v['values'],
    }, false);

    chart.yAxis[0].setTitle({
      text: `${v['type']} (${v['units']})`,
    });
  });
}

/**
 * Display error message on chart
 * @param {object} chart - Highcharts instance
 * @param {string} message - Error message to display
 */
function showChartError(chart, message) {
  const textX = chart.plotLeft + (chart.plotWidth / 2);
  const textY = chart.plotTop + (chart.plotHeight / 2);

  // Remove existing error message if any
  if (chart.errorText) {
    chart.errorText.destroy();
  }

  chart.errorText = chart.renderer.text(message, textX, textY)
    .css({
      color: '#cc0000',
      fontSize: '16px',
      fontWeight: 'bold',
      textAlign: 'center',
    })
    .attr({
      align: 'center',
    })
    .add();
}

/**
 * Load and display sensor time series data
 * @param {object} sensorInfo - Sensor information (sku, device, deviceId)
 */
export function loadSensorTimeseries(sensorInfo) {
  const chart = getChart('timeSeriesSensors');
  if (!chart) {
    console.error('Sensor time series chart not initialized');
    return;
  }

  // Show loading indicator
  if (chart.noData) {
    chart.noData.destroy();
  }

  // Remove existing series
  for (let i = chart.series.length - 1; i >= 0; i--) {
    chart.series[i].remove(false);
  }

  chart.showLoading();

  // Step 1: Request data task from Govee API
  const url = `${API_BASE}/govee-th-datatask`;

  jQuery.getJSON(url, sensorInfo, (taskResponse) => {
    // Check if response has error
    if (taskResponse.error) {
      console.error('[TimeSeries] Sensor task error:', taskResponse.error, taskResponse.message);
      chart.hideLoading();
      showChartError(chart, `Error: ${  taskResponse.message || taskResponse.error}`);
      return;
    }

    // Check if response has expected structure
    if (!taskResponse.data || !taskResponse.data.taskIds || !taskResponse.data.taskIds[0]) {
      console.error('[TimeSeries] Invalid task response:', taskResponse);
      chart.hideLoading();
      showChartError(chart, 'Invalid response from sensor API');
      return;
    }

    // Extract task ID from response
    const taskId = taskResponse['data']['taskIds'][0];

    // Step 2: Poll for task completion
    checkSensorDataTaskStatus(chart, {
      sku: sensorInfo.sku,
      device: sensorInfo.device,
      task: taskId,
    });
  }).fail((jqXHR, textStatus, errorThrown) => {
    console.error('[TimeSeries] Failed to create task:', textStatus, errorThrown, jqXHR.status);

    // Try to parse error response
    let errorMsg = 'Failed to load sensor data';
    try {
      const errorResponse = JSON.parse(jqXHR.responseText);
      if (errorResponse.message) {
        errorMsg = errorResponse.message;
      }
    } catch {
      // Use default error message
    }

    chart.hideLoading();
    showChartError(chart, errorMsg);
  });
}

/**
 * Poll for sensor data task completion
 * @param {object} chart - Highcharts instance
 * @param {object} taskInfo - Task information (sku, device, task)
 */
function checkSensorDataTaskStatus(chart, taskInfo) {
  const url = `${API_BASE}/govee-th-datatask-status`;

  jQuery.getJSON(url, taskInfo, (data) => {
    if (data['status'] !== 'finished') {
      // Task not finished yet, poll again in 1 second
      setTimeout(() => checkSensorDataTaskStatus(chart, taskInfo), 1000);
    } else {
      // Task finished, display the data
      displaySensorData(chart, data);
    }
  }).fail((jqXHR, textStatus, errorThrown) => {
    console.error('[TimeSeries] Failed to check task status:', textStatus, errorThrown, jqXHR.status);
    chart.hideLoading();
  });
}

/**
 * Display sensor data on chart
 * @param {object} chart - Highcharts instance
 * @param {object} data - Sensor data response
 */
function displaySensorData(chart, data) {
  chart.hideLoading();

  // Check if data is empty
  if (!data['d'] || data['d'].length < 1) {
    const textX = chart.plotLeft + (chart.plotWidth / 2);
    const textY = chart.plotTop + (chart.plotHeight / 2);

    chart.noData = chart.renderer.text('No Data Available', textX, textY)
      .css({
        color: '#666666',
        fontSize: '16px',
      })
      .add();
    return;
  }

  // Add each series to its own stacked pane: temperature on the top axis,
  // humidity on the bottom axis.
  jQuery.each(data['d'], (k, v) => {
    if (v['name'] === 'Temperature') {
      chart.addSeries({
        type: 'line',
        name: v['name'],
        color: '#FF0000',
        data: v['values'],
        yAxis: 0,
      }, false);

      chart.yAxis[0].setTitle({ text: `${v['type']} (${v['units']})` });
    } else if (v['name'] === 'Humidity') {
      chart.addSeries({
        type: 'line',
        name: v['name'],
        color: '#39ab00',
        data: v['values'],
        yAxis: 1,
      }, false);

      chart.yAxis[1].setTitle({ text: `${v['type']} (${v['units']})` });
    }
  });

  // Update chart range and redraw
  chart.xAxis[0].update({ max: Date.now() });
  chart.zoomOut();
  chart.redraw();
}
